import React, { useEffect, useState } from "react";
import { Eye, Users, Clock, Spinner, Link as LinkIcon, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const APP_URL = process.env.REACT_APP_BACKEND_URL;

const _timeAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/**
 * Owner-only view analytics for a shared audit link. Polls once on mount and
 * once every 30s while mounted so a fresh view surfaces without a manual refresh.
 */
export default function AuditAnalyticsCard({ lead }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const shareUrl = lead?.audit_share_token
    ? `${APP_URL}/audit/${lead.audit_share_token}`
    : "";

  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetch = () => {
      if (!lead?.id) return;
      api
        .get(`/leads/${lead.id}/audit-analytics`)
        .then((r) => !cancelled && setData(r.data))
        .catch(() => {})
        .finally(() => !cancelled && setLoading(false));
    };
    fetch();
    timer = setInterval(fetch, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [lead?.id]);

  if (!lead?.audit_share_token) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Share link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm"
      data-testid="audit-analytics-card"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2 text-xs text-zinc-300">
          <Eye size={14} weight="duotone" className="text-orange-400" />
          Audit link analytics
        </div>
        <button
          type="button"
          data-testid="audit-share-copy"
          onClick={copyLink}
          className="h-7 flex items-center gap-1 text-[11px] font-mono text-zinc-400 hover:text-orange-400 transition-colors px-2"
        >
          {copied ? "Copied!" : (<><Copy size={11} /> Copy link</>)}
        </button>
      </div>

      <div className="p-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Spinner size={12} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Metric
                icon={<Eye size={12} className="text-orange-400" weight="duotone" />}
                label="Views"
                value={data?.total_views ?? 0}
                testid="analytics-views"
              />
              <Metric
                icon={<Users size={12} className="text-emerald-400" weight="duotone" />}
                label="Visitors"
                value={data?.unique_visitors ?? 0}
                testid="analytics-visitors"
              />
              <Metric
                icon={<Clock size={12} className="text-amber-400" weight="duotone" />}
                label="Last seen"
                value={_timeAgo(data?.last_viewed_at)}
                testid="analytics-last-seen"
                mono
              />
            </div>

            {data?.recent?.length > 0 ? (
              <div className="border-t border-zinc-800 pt-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
                  Recent opens
                </div>
                <div
                  className="space-y-1 max-h-28 overflow-y-auto pr-1"
                  data-testid="analytics-recent"
                >
                  {data.recent.slice(0, 5).map((r, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between text-[11px] font-mono text-zinc-400"
                    >
                      <span>{_timeAgo(r.viewed_at)}</span>
                      <span className="text-zinc-500">{r.client}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500 border-t border-zinc-800 pt-2 flex items-center gap-1.5">
                <LinkIcon size={11} /> No opens yet. Send the outreach to start tracking.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ icon, label, value, testid, mono }) {
  return (
    <div
      className="bg-zinc-950/60 border border-zinc-800 rounded-sm px-2.5 py-2"
      data-testid={testid}
    >
      <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-500">
        {icon} {label}
      </div>
      <div
        className={`text-sm mt-0.5 tabular-nums text-zinc-100 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}
